import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ApiError, getAlbum, type Album } from "../lib/api";
import { formatCountdown, galleryVisibleAt } from "../lib/timewindow";
import { EventPage, EventShell } from "../components/EventPage";
import { Gallery } from "../components/Gallery";
import { useT } from "../lib/i18n";

export const Route = createFileRoute("/a/$slug")({
  // ?join=camera: datang dari /camera tanpa token. Tamu tanpa token selalu melihat layar join, lalu diarahkan ke kamera.
  validateSearch: (s: Record<string, unknown>): { join?: "camera" } => (s.join === "camera" ? { join: "camera" } : {}),
  component: GuestEventPage,
});

function GuestEventPage() {
  const { t } = useT();
  const { slug } = Route.useParams();
  const album = useQuery({ queryKey: ["album", slug], queryFn: () => getAlbum(slug), retry: false });

  if (album.isPending) return <EventShell>{t("common.loading")}</EventShell>;
  if (album.error) {
    const notFound = album.error instanceof ApiError && album.error.status === 404;
    return <EventShell>{notFound ? t("errors.album_not_found") : t("guest.load_failed")}</EventShell>;
  }
  return (
    <EventPage album={album.data}>
      <GuestPhotos album={album.data} />
    </EventPage>
  );
}

function GuestPhotos({ album }: { album: Album }) {
  const { t } = useT();
  // ponytail: gallery_mode hanya ditegakkan di klien (desain teknis §7 menerima ini);
  // pemegang link tetap bisa memanggil API langsung.
  const visibleAt = galleryVisibleAt(album);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (now >= visibleAt) return;
    const id = setTimeout(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearTimeout(id);
  }, [now, visibleAt]);

  if (album.settings.visibility === "own")
    return <p className="text-center text-sm text-neutral-400">{t("guest.gallery.own_only")}</p>;
  if (now < visibleAt)
    return (
      <div className="space-y-1 rounded-2xl border border-white/10 p-5 text-center">
        <p className="text-sm text-neutral-400">{t("guest.gallery.countdown_label")}</p>
        <p className="text-lg font-semibold">{formatCountdown(visibleAt - now)}</p>
      </div>
    );
  return <Gallery slug={album.slug} album={album} />;
}
